import React, { useState, type JSX } from 'react';
import { getInvoiceActionsItems } from '../InvoiceActions';
import type { OnInvoiceActionCallback } from '../InvoiceActions';
import { headCells } from './data';
import { formatCurrencyValue } from '../../../../../util/PaymentUtils';
import {
  BookingStatus,
  BookingTransactionStatusTypeLabel,
  BookingTransactionTypeLabel,
  ServiceFeeTypeLabel,
} from '../../../../constants/securspace-constants';
import {
  BuyerInvoiceStatusGroup,
  SubscriptionType,
  SupplierInvoiceStatusGroup,
} from '../../../../constants/booking-transaction-constants';
import type { Account } from '../../../../../types/Account';
import type { InvoiceTransactionVO as AdminInvoiceTransaction } from '../../../../../types/model/valueObject/invoice/InvoiceTransactionVO';
import InfoCard from "../../../../deprecatedUikit/InfoCard/InfoCard";

interface InvoiceCardProps {
  account: Account;
  invoice: AdminInvoiceTransaction;
  onInvoiceAction: OnInvoiceActionCallback;
}

/**
 * Displays a formatted admin invoice card with all transaction details and available actions.
 *
 * @param account The currently logged-in admin account.
 * @param invoice The admin invoice transaction to display.
 * @param onInvoiceAction Callback invoked when an invoice action completes or a dialog is opened.
 *
 * @see AdminInvoiceTransaction for the expected shape of the invoice prop.
 */
const InvoiceCard = ({ account, invoice, onInvoiceAction }: InvoiceCardProps): JSX.Element => {
  const [expanded, setExpanded] = useState(false);

  const formattedInvoice = {
    ...invoice,
    bookingStatus: BookingStatus[invoice.bookingStatus as keyof typeof BookingStatus] || invoice.bookingStatus,
    buyerAmount: formatCurrencyValue(invoice.buyerAmount, true),
    buyerTransactionStatus: BuyerInvoiceStatusGroup[invoice.buyerTransactionStatus] ? BuyerInvoiceStatusGroup[invoice.buyerTransactionStatus].label : invoice.buyerTransactionStatus,
    locationAddress: `${invoice.locationAddressLine1}, ${invoice.locationCity}, ${invoice.locationState} ${invoice.locationZip}`,
    locationIsLive: invoice.locationIsLive ? 'True' : 'False',
    paymentProcessorFees: formatCurrencyValue(invoice.paymentProcessorFees, true),
    securspaceFees: formatCurrencyValue(invoice.securspaceFees, true),
    serviceFeeType: ServiceFeeTypeLabel[invoice.serviceFeeType as keyof typeof ServiceFeeTypeLabel] || invoice.serviceFeeType,
    status: BookingTransactionStatusTypeLabel[invoice.status as keyof typeof BookingTransactionStatusTypeLabel] || invoice.status,
    supplierAmount: formatCurrencyValue(invoice.supplierAmount, true),
    supplierTransactionStatus: SupplierInvoiceStatusGroup[invoice.supplierTransactionStatus]
      ? SupplierInvoiceStatusGroup[invoice.supplierTransactionStatus].label
      : invoice.supplierTransactionStatus,
    supplierPayoutOnHold: invoice.supplierPayoutOnHold ? 'True' : 'False',
    supplierSubscriptionType: SubscriptionType[invoice.supplierSubscriptionType]?.label || invoice.supplierSubscriptionType,
    transactionTaxAmount: formatCurrencyValue(invoice.transactionTaxAmount, true),
    transactionType: BookingTransactionTypeLabel[invoice.transactionType as keyof typeof BookingTransactionTypeLabel] || invoice.transactionType,
  };

  return (
    <InfoCard
      data={formattedInvoice}
      actionItems={getInvoiceActionsItems(account, invoice, onInvoiceAction)}
      headCells={headCells}
      collapse={expanded}
      handleToggle={() => setExpanded(!expanded)}
      links={{ bookingNumber: { baseUrl: '/admin-bookings', queryParam: 'bookingNumber' } }}
    />
  );
};

export default InvoiceCard;
